import type {
    CarWashState,
    Customer,
    Invoice,
    Job,
    JobEvent,
    Payment,
    PaymentMethod,
    Service,
    ServiceIcon,
    Staff,
    Vehicle
} from '@/types'

import { DEFAULT_TEMPLATES } from '@/constants/carwash'
import { calculateTotals } from '@/lib/carwash/billing'
import {
    HOUR,
    MINUTE,
    addDays,
    startOfDay
} from '@/lib/carwash/format'
import { createId, formatInvoiceNumber } from '@/lib/carwash/ids'

/*
 * Demo data for a busy car wash in Kannur, Kerala. Everything is generated
 * relative to the moment the app is first opened, so the board always looks
 * like a live working day: two cars washing, one waiting, a day of finished
 * jobs, and thirty days of history for the reports.
 */

const createRandom = (seed: number) => {
    let value = seed >>> 0

    return (): number => {
        value = (value + 0x6d2b79f5) >>> 0
        let t = value
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const pickWeighted = <T>(random: () => number, items: { value: T, weight: number }[]): T => {
    const total = items.reduce((sum, item) => sum + item.weight, 0)
    let roll = random() * total

    for (const item of items) {
        roll -= item.weight
        if (roll <= 0) return item.value
    }

    return items[items.length - 1].value
}

const iso = (time: number): string => new Date(time).toISOString()

type FleetEntry = {
    plate: string
    model: string
    owner?: string
    phone?: string
    photo?: string
}

const FLEET: FleetEntry[] = [
    { plate: 'KL13AB1234', model: 'Hyundai Creta', owner: 'Muhammed Faisal', phone: '9847031245' },
    { plate: 'KL58C4567', model: 'Maruti Swift', photo: '/images/vehicles/hatchback-green.jpg' },
    { plate: 'KL13D7890', model: 'Honda City', owner: 'Anjali Nair', phone: '9895104422', photo: '/images/vehicles/sedan-grey.jpg' },
    { plate: 'KL13AK5521', model: 'Toyota Innova Crysta', owner: 'Joseph Thomas', phone: '9446217730' },
    { plate: 'KL59F2210', model: 'Mahindra XUV700', owner: 'Fathima Rinsha' },
    { plate: 'KL13BC3381', model: 'Tata Nexon', owner: 'Arjun Menon', phone: '9745520981' },
    { plate: 'KL86A9012', model: 'Kia Seltos', owner: 'Sreejith K', phone: '9562338104' },
    { plate: 'KL58H7745', model: 'Maruti Baleno' },
    { plate: 'KL13AL0456', model: 'Hyundai i20', owner: 'Divya Prakash', phone: '9847665210' },
    { plate: 'KL78B6634', model: 'Toyota Fortuner', owner: 'Abdul Rasheed', phone: '9447302918' },
    { plate: 'KL13Y4410', model: 'Maruti Ertiga', owner: 'Nikhil Raj' },
    { plate: 'KL59K3092', model: 'Tata Punch' },
    { plate: 'KL13AN7701', model: 'Mahindra Thar', owner: 'Shabna Hameed', phone: '9605118842' },
    { plate: 'KL58M2287', model: 'Hyundai Venue', owner: 'Vivek Nambiar', phone: '9895442071' },
    { plate: 'KL14S8813', model: 'Maruti Dzire' },
    { plate: 'KL13AC6650', model: 'Honda Amaze', owner: 'Rahul Krishnan', phone: '9746093315' },
    { plate: 'KL86C1179', model: 'Kia Sonet' },
    { plate: 'KL13AP2045', model: 'MG Hector', owner: 'Ayesha Siddiqui', phone: '9847220476' },
    { plate: 'KL11BD5590', model: 'Toyota Glanza' },
    { plate: 'KL59P4478', model: 'Maruti Brezza', owner: 'Thomas Kurian', phone: '9447851236' },
    { plate: 'KL13AR9126', model: 'Skoda Slavia', owner: 'Haris PK' },
    { plate: 'KL58N6302', model: 'Volkswagen Virtus' },
    { plate: 'KL13Q2857', model: 'Renault Kwid', owner: 'Lakshmi Varma', phone: '9562784410' },
    { plate: 'KL78D4415', model: 'Maruti Wagon R' },
    { plate: 'KL13AS3368', model: 'Hyundai Verna', owner: 'Sajid Ali', phone: '9895370652' },
    { plate: 'KL18T7792', model: 'Mahindra Scorpio-N' },
    { plate: 'KL13AT1149', model: 'Tata Harrier', owner: 'Neethu Mohan', phone: '9745181937' },
    { plate: 'KL59R8836', model: 'Toyota Hyryder' },
    { plate: 'KL13U5523', model: 'Maruti Alto K10' },
    { plate: 'KL58P9071', model: 'Honda Elevate', owner: 'Rinoy George', phone: '9446631208' },
    { plate: 'KA19MH4521', model: 'Hyundai Creta' },
    { plate: '22BH6412C', model: 'Kia Carens', owner: 'Deepak Kumar', phone: '9846517723' },
    { plate: 'KL13AV2210', model: 'Maruti Swift' },
    { plate: 'KL86E3347', model: 'Tata Tiago', owner: 'Ameen Kunhi' },
    { plate: 'KL13AW8854', model: 'Hyundai Exter' },
    { plate: 'KL59S1186', model: 'Maruti Fronx', owner: 'Gopika Suresh', phone: '9562450087' },
    { plate: 'KL13AX4409', model: 'Toyota Innova Hycross' },
    { plate: 'KL58R2263', model: 'Mahindra Bolero' },
    { plate: 'KL14U7718', model: 'Maruti Celerio' },
    { plate: 'KL13AY3305', model: 'Honda City', owner: 'Faris Muhammed', phone: '9847906631' },
    { plate: 'KL78F5590', model: 'Tata Altroz' },
    { plate: 'KL13AZ6672', model: 'Kia Seltos' },
    { plate: 'KL59T2241', model: 'Hyundai Alcazar', owner: 'Sandeep Pillai', phone: '9447560219' },
    { plate: 'KL13BA9983', model: 'Maruti Ciaz' },
    { plate: 'KL11CF1437', model: 'Mahindra XUV 3XO' },
    { plate: 'KL13BB5126', model: 'Toyota Camry', owner: 'Dr. Ramesh Babu', phone: '9846002781' },
    { plate: 'KL58S8809', model: 'Maruti Dzire' },
    { plate: 'KL13BD2368', model: 'Jeep Compass' }
]

const SERVICES: { name: string, description: string, price: number, durationMinutes: number, icon: ServiceIcon, weight: number }[] = [
    { name: 'Basic Wash', description: 'Exterior rinse, foam wash and wipe dry.', price: 150, durationMinutes: 15, icon: 'droplets', weight: 26 },
    { name: 'Exterior Wash', description: 'Foam wash, tyre and rim cleaning, glass polish.', price: 200, durationMinutes: 20, icon: 'spray', weight: 20 },
    { name: 'Interior Cleaning', description: 'Vacuum, dashboard and seat wipe-down, mats cleaned.', price: 250, durationMinutes: 30, icon: 'sofa', weight: 12 },
    { name: 'Premium Wash', description: 'Foam wash, interior vacuum, tyre shine and dashboard polish.', price: 350, durationMinutes: 35, icon: 'sparkles', weight: 26 },
    { name: 'Full Wash', description: 'Complete interior and exterior detailing with a wax finish.', price: 500, durationMinutes: 50, icon: 'gem', weight: 16 }
]

const STAFF: { name: string, phone: string | null }[] = [
    { name: 'Rahman', phone: '9847012451' },
    { name: 'Shameer', phone: '9895023417' },
    { name: 'Ashraf', phone: null },
    { name: 'Niyas', phone: '9562047719' }
]

const PAYMENT_WEIGHTS: { value: PaymentMethod, weight: number }[] = [
    { value: 'upi', weight: 55 },
    { value: 'cash', weight: 33 },
    { value: 'card', weight: 10 },
    { value: 'other', weight: 2 }
]

const OPEN_HOUR = 8
const CLOSE_HOUR = 20

type DraftJob = {
    job: Job
    invoice: Invoice | null
    payment: Payment | null
}

export const createDemoState = (current: Date = new Date()): CarWashState => {
    const random = createRandom(20261102)
    const now = current.getTime()
    const nowIso = iso(now)
    const today = startOfDay(now)
    const firstDay = addDays(today, -30).getTime()

    const services: Service[] = SERVICES.map((item): Service => ({
        id: createId(),
        name: item.name,
        description: item.description,
        price: item.price,
        durationMinutes: item.durationMinutes,
        icon: item.icon,
        active: true,
        createdAt: iso(firstDay),
        updatedAt: iso(firstDay)
    }))

    const serviceWeights = services.map((service, index) => ({ value: service, weight: SERVICES[index].weight }))
    const serviceByName = new Map(services.map(service => [service.name, service]))

    const dutyStart = Math.min(now - HOUR, today.getTime() + OPEN_HOUR * HOUR)

    const staff: Staff[] = STAFF.map((item): Staff => ({
        id: createId(),
        name: item.name,
        phone: item.phone,
        photo: null,
        duty: 'on_duty',
        dutySince: iso(dutyStart),
        createdAt: iso(firstDay),
        updatedAt: iso(firstDay)
    }))

    const [rahman, shameer, ashraf, niyas] = staff

    const customers: Customer[] = []
    const vehicles: Vehicle[] = FLEET.map(entry => {
        let customerId: string | null = null

        if (entry.owner || entry.phone) {
            const customer: Customer = {
                id: createId(),
                name: entry.owner ?? null,
                phone: entry.phone ?? null,
                createdAt: iso(firstDay),
                updatedAt: iso(firstDay)
            }

            customers.push(customer)
            customerId = customer.id
        }

        return {
            id: createId(),
            plate: entry.plate,
            model: entry.model,
            customerId,
            photo: entry.photo ?? null,
            createdAt: iso(firstDay),
            updatedAt: iso(firstDay)
        }
    })

    const activeVehicleIds = new Set(vehicles.slice(0, 3).map(vehicle => vehicle.id))
    const vehicleWeights = vehicles.map((vehicle, index) => ({
        value: vehicle,
        weight: index < 12 ? 6 : index < 30 ? 3 : 1.5
    }))

    const drafts: DraftJob[] = []

    const cutoffFor = (staffId: string): number => {
        if (staffId === rahman.id) return now - 20 * MINUTE
        if (staffId === ashraf.id) return now - 11 * MINUTE
        return now - 3 * MINUTE
    }

    for (let offset = 29; offset >= 0; offset -= 1) {
        const day = addDays(today, -offset)
        const open = day.getTime() + OPEN_HOUR * HOUR
        const close = day.getTime() + CLOSE_HOUR * HOUR
        const weekday = day.getDay()

        let count = 10 + Math.floor(random() * 5) + Math.round((29 - offset) / 12)
        if (weekday === 0) count += 5
        if (weekday === 6) count += 3
        if (weekday === 2) count -= 2

        let windowEnd = close

        if (offset === 0) {
            windowEnd = Math.min(close, now - 30 * MINUTE)
            if (windowEnd <= open + 20 * MINUTE) continue
            count = Math.max(1, Math.round((count * (windowEnd - open)) / (close - open)))
        }

        const arrivals = Array.from({ length: count }, () => open + random() * (windowEnd - open)).sort((a, b) => a - b)
        const freeAt = new Map(staff.map(member => [member.id, open]))
        const usedToday = new Set<string>()

        for (const arrival of arrivals) {
            let vehicle = pickWeighted(random, vehicleWeights)

            for (let attempt = 0; attempt < 8 && (usedToday.has(vehicle.id) || (offset < 4 && activeVehicleIds.has(vehicle.id))); attempt += 1) {
                vehicle = pickWeighted(random, vehicleWeights)
            }

            if (usedToday.has(vehicle.id) || (offset < 4 && activeVehicleIds.has(vehicle.id))) continue

            const member = [...staff].sort((a, b) => (freeAt.get(a.id) ?? 0) - (freeAt.get(b.id) ?? 0) || random() - 0.5)[0]
            const service = pickWeighted(random, serviceWeights)
            const startAt = Math.max(arrival + (1 + random() * 3) * MINUTE, freeAt.get(member.id) ?? open)
            const duration = service.durationMinutes * (0.85 + random() * 0.4) * MINUTE
            const finishAt = startAt + duration

            if (offset === 0 && finishAt > cutoffFor(member.id)) continue
            if (finishAt > close + 45 * MINUTE) continue

            usedToday.add(vehicle.id)
            freeAt.set(member.id, finishAt + (2 + random() * 4) * MINUTE)

            const withQualityCheck = service.durationMinutes >= 35 || random() < 0.25
            const qualityCheckAt = withQualityCheck ? finishAt - (3 + random() * 3) * MINUTE : null
            const assignedAt = Math.max(arrival, startAt - MINUTE)

            const events: JobEvent[] = [
                { at: iso(arrival), type: 'registered' },
                { at: iso(assignedAt), type: 'assigned', staffId: member.id },
                { at: iso(startAt), type: 'status', status: 'washing' }
            ]

            if (qualityCheckAt) events.push({ at: iso(qualityCheckAt), type: 'status', status: 'quality_check' })
            events.push({ at: iso(finishAt), type: 'completed', staffId: member.id })

            const jobId = createId()
            const invoiceId = createId()
            const discounted = random() < 0.07
            const discountType = random() < 0.5 ? 'percent' : 'amount'
            const discountValue = discounted ? (discountType === 'percent' ? 10 : 50) : 0
            const lines = [{ label: service.name, amount: service.price }]
            const totals = calculateTotals(lines, discountType, discountValue)
            const paidAt = finishAt + (1 + random() * 7) * MINUTE
            const method = pickWeighted(random, PAYMENT_WEIGHTS)
            const paid = paidAt < now - MINUTE

            if (paid) events.push({ at: iso(paidAt), type: 'paid', note: method })

            const job: Job = {
                id: jobId,
                number: 0,
                vehicleId: vehicle.id,
                plate: vehicle.plate,
                vehicleModel: vehicle.model,
                customerId: vehicle.customerId,
                photo: vehicle.photo,
                serviceId: service.id,
                serviceName: service.name,
                price: service.price,
                estimatedMinutes: service.durationMinutes,
                staffId: member.id,
                status: 'completed',
                enteredAt: iso(arrival),
                assignedAt: iso(assignedAt),
                startedAt: iso(startAt),
                qualityCheckAt: qualityCheckAt ? iso(qualityCheckAt) : null,
                completedAt: iso(finishAt),
                cancelledAt: null,
                invoiceId,
                events,
                createdAt: iso(arrival),
                updatedAt: iso(paid ? paidAt : finishAt)
            }

            const invoice: Invoice = {
                id: invoiceId,
                number: '',
                jobId,
                vehicleId: vehicle.id,
                customerId: vehicle.customerId,
                plate: vehicle.plate,
                vehicleModel: vehicle.model,
                serviceName: service.name,
                lines,
                subtotal: totals.subtotal,
                discountType: discounted ? discountType : 'amount',
                discountValue,
                discountAmount: totals.discountAmount,
                total: totals.total,
                status: paid ? 'paid' : 'pending',
                paymentMethod: paid ? method : null,
                issuedAt: iso(finishAt),
                paidAt: paid ? iso(paidAt) : null,
                createdAt: iso(finishAt),
                updatedAt: iso(paid ? paidAt : finishAt)
            }

            const payment: Payment | null = paid
                ? { id: createId(), invoiceId, amount: totals.total, method, receivedAt: iso(paidAt), voidedAt: null, createdAt: iso(paidAt) }
                : null

            drafts.push({ job, invoice, payment })
        }
    }

    /* A few unpaid bills: the latest one from today, one from earlier today and one from yesterday. */
    const todayStart = today.getTime()
    const yesterdayStart = addDays(today, -1).getTime()
    const todays = drafts.filter(draft => new Date(draft.job.completedAt ?? 0).getTime() >= todayStart)
    const yesterdays = drafts.filter(draft => {
        const finished = new Date(draft.job.completedAt ?? 0).getTime()
        return finished >= yesterdayStart && finished < todayStart
    })

    const markPending = (draft: DraftJob | undefined) => {
        if (!draft || !draft.invoice) return

        draft.invoice = { ...draft.invoice, status: 'pending', paymentMethod: null, paidAt: null, updatedAt: draft.invoice.issuedAt }
        draft.payment = null
        draft.job = { ...draft.job, events: draft.job.events.filter(event => event.type !== 'paid'), updatedAt: draft.invoice.issuedAt }
    }

    const byFinish = (a: DraftJob, b: DraftJob) => new Date(a.job.completedAt ?? 0).getTime() - new Date(b.job.completedAt ?? 0).getTime()

    todays.sort(byFinish)
    markPending(todays[todays.length - 1])
    if (todays.length > 5) markPending(todays[todays.length - 5])
    if (yesterdays.length) markPending(yesterdays[Math.floor(yesterdays.length / 2)])

    /* Active jobs on the board right now. */
    const [creta, swift, city] = vehicles
    const premium = serviceByName.get('Premium Wash') as Service
    const basic = serviceByName.get('Basic Wash') as Service
    const full = serviceByName.get('Full Wash') as Service

    const activeJob = (vehicle: Vehicle, service: Service, member: Staff | null, enteredAgo: number, startedAgo: number | null): Job => {
        const enteredAt = iso(now - enteredAgo * MINUTE)
        const startedAt = startedAgo === null ? null : iso(now - startedAgo * MINUTE)
        const assignedAt = startedAgo === null ? null : iso(now - (startedAgo + 1) * MINUTE)
        const events: JobEvent[] = [{ at: enteredAt, type: 'registered' }]

        if (member && assignedAt && startedAt) {
            events.push({ at: assignedAt, type: 'assigned', staffId: member.id })
            events.push({ at: startedAt, type: 'status', status: 'washing' })
        }

        return {
            id: createId(),
            number: 0,
            vehicleId: vehicle.id,
            plate: vehicle.plate,
            vehicleModel: vehicle.model,
            customerId: vehicle.customerId,
            photo: vehicle.photo,
            serviceId: service.id,
            serviceName: service.name,
            price: service.price,
            estimatedMinutes: service.durationMinutes,
            staffId: member?.id ?? null,
            status: member ? 'washing' : 'waiting',
            enteredAt,
            assignedAt,
            startedAt,
            qualityCheckAt: null,
            completedAt: null,
            cancelledAt: null,
            invoiceId: null,
            events,
            createdAt: enteredAt,
            updatedAt: startedAt ?? enteredAt
        }
    }

    const cretaJob = activeJob(creta, premium, rahman, 22, 18)
    const swiftJob = activeJob(swift, basic, ashraf, 11, 9)
    const cityJob = activeJob(city, full, null, 6, null)

    const receivedAt = iso(now - 20 * MINUTE)
    const cretaOwner = customers.find(customer => customer.id === creta.customerId)

    cretaJob.events.push({ at: receivedAt, type: 'message', note: 'received' })
    cretaJob.events.sort((a, b) => a.at.localeCompare(b.at))

    /* Number everything in the order it happened. */
    const completedJobs = drafts.map(draft => draft.job).sort((a, b) => a.enteredAt.localeCompare(b.enteredAt))
    const jobs = [...completedJobs, cretaJob, swiftJob, cityJob]

    jobs.forEach((job, index) => {
        job.number = index + 1
    })

    const invoiceStart = 1001
    const invoices = drafts
        .map(draft => draft.invoice)
        .filter((invoice): invoice is Invoice => Boolean(invoice))
        .sort((a, b) => a.issuedAt.localeCompare(b.issuedAt))
        .map((invoice, index) => ({ ...invoice, number: formatInvoiceNumber('BW-', invoiceStart + index) }))

    const payments = drafts
        .map(draft => draft.payment)
        .filter((payment): payment is Payment => Boolean(payment))
        .sort((a, b) => a.receivedAt.localeCompare(b.receivedAt))

    /* Vehicles only exist once they have visited; keep their dates honest. */
    const firstVisit = new Map<string, string>()
    const lastPhoto = new Map<string, string>()

    for (const job of jobs) {
        if (!firstVisit.has(job.vehicleId)) firstVisit.set(job.vehicleId, job.enteredAt)
        if (job.photo) lastPhoto.set(job.vehicleId, job.photo)
    }

    const visitedVehicles = vehicles
        .filter(vehicle => firstVisit.has(vehicle.id))
        .map(vehicle => ({
            ...vehicle,
            photo: lastPhoto.get(vehicle.id) ?? vehicle.photo,
            createdAt: firstVisit.get(vehicle.id) ?? vehicle.createdAt,
            updatedAt: firstVisit.get(vehicle.id) ?? vehicle.updatedAt
        }))

    const linkedCustomers = new Set(visitedVehicles.map(vehicle => vehicle.customerId).filter(Boolean))
    const visitedCustomers = customers
        .filter(customer => linkedCustomers.has(customer.id))
        .map(customer => {
            const vehicle = visitedVehicles.find(item => item.customerId === customer.id)
            return vehicle ? { ...customer, createdAt: vehicle.createdAt, updatedAt: vehicle.createdAt } : customer
        })

    return {
        version: 1,
        settings: {
            name: 'Bluewave Car Wash',
            logo: null,
            address: 'Fort Road, Kannur, Kerala 670001',
            phone: '0497 270 4455',
            taxId: '',
            currency: 'INR',
            countryCode: '91',
            invoicePrefix: 'BW-',
            nextInvoiceNumber: invoiceStart + invoices.length,
            invoiceFooter: 'Thank you for choosing Bluewave. Drive safe!',
            defaultPaymentMethod: 'cash',
            templates: { ...DEFAULT_TEMPLATES },
            updatedAt: nowIso
        },
        services,
        staff: [rahman, shameer, ashraf, niyas],
        customers: visitedCustomers,
        vehicles: visitedVehicles,
        jobs,
        invoices,
        payments,
        messages: cretaOwner?.phone
            ? [{
                id: createId(),
                jobId: cretaJob.id,
                invoiceId: null,
                customerId: cretaOwner.id,
                phone: `91${cretaOwner.phone}`,
                template: 'received',
                body: DEFAULT_TEMPLATES.received
                    .replace('{vehicle}', 'KL 13 AB 1234')
                    .replace('{service}', premium.name)
                    .replace('{business}', 'Bluewave Car Wash'),
                channel: 'whatsapp_link',
                openedAt: receivedAt
            }]
            : [],
        nextJobNumber: jobs.length + 1,
        createdAt: nowIso
    }
}

/*
 * A clean start for going live: keeps services, staff and business settings,
 * and removes every vehicle, customer, bill and message.
 */
export const createFreshState = (from: CarWashState, current: Date = new Date()): CarWashState => {
    const nowIso = current.toISOString()

    return {
        ...from,
        settings: { ...from.settings, nextInvoiceNumber: 1, updatedAt: nowIso },
        staff: from.staff.map((member): Staff => ({ ...member, duty: member.duty === 'inactive' ? 'inactive' : 'on_duty', dutySince: nowIso })),
        customers: [],
        vehicles: [],
        jobs: [],
        invoices: [],
        payments: [],
        messages: [],
        nextJobNumber: 1,
        createdAt: nowIso
    }
}
